import { useEffect, useState } from 'react';
import { Lottie } from '@/components/ui/lottie';
import { cn } from '@/lib/utils';
import searchAnimation from '@/assets/json/search_300x150-2.json';
import loaderCircle from '@/assets/json/Loader_Circle_24x24_Black.json';
import processingLogos from '../assets/processing-logos.webp';
import processingCheck from '../assets/processing-check.svg';
import {
  PROCESSING_CHECKLIST,
  PROCESSING_COMPLETE_HOLD_MS,
  PROCESSING_ITEM_DURATION_MS,
} from '../constants';

type ItemStatus = 'pending' | 'active' | 'done';

interface ProcessingEstimatingProps {
  onComplete: () => void;
}

export function ProcessingEstimating({ onComplete }: ProcessingEstimatingProps) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [doneCount, setDoneCount] = useState(0);

  // All items start blurred; then the first unblurs and starts spinning.
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setActiveIndex(0));
    return () => window.cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (activeIndex < 0 || activeIndex >= PROCESSING_CHECKLIST.length) return;

    const doneTimer = window.setTimeout(() => {
      setDoneCount((prev) => prev + 1);
      setActiveIndex((prev) => prev + 1);
    }, PROCESSING_ITEM_DURATION_MS);

    return () => window.clearTimeout(doneTimer);
  }, [activeIndex]);

  useEffect(() => {
    if (doneCount < PROCESSING_CHECKLIST.length) return;
    const hold = window.setTimeout(onComplete, PROCESSING_COMPLETE_HOLD_MS);
    return () => window.clearTimeout(hold);
  }, [doneCount, onComplete]);

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-mf-neutral-50 px-4 py-4 font-body">
      <div className="flex w-full flex-col items-center gap-6">
        <div className="relative h-[150px] w-[300px] shrink-0 overflow-hidden">
          <img
            src={processingLogos}
            alt=""
            className="absolute inset-0 size-full object-cover opacity-90"
          />
          <Lottie
            animationData={searchAnimation}
            className="absolute inset-0 size-full"
          />
        </div>

        <div className="w-full text-center">
          <h1 className="font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
            Estimating
            <br />
            Your Payouts
          </h1>
          <p className="mt-2 text-base leading-5 text-mf-neutral-600">
            Matching your answers against thousands of active settlements
          </p>
        </div>

        <ul className="flex flex-col items-center gap-2">
          {PROCESSING_CHECKLIST.map((label, index) => {
            const status: ItemStatus =
              index < doneCount ? 'done' : index === activeIndex ? 'active' : 'pending';

            return (
              <li
                key={label}
                className={cn(
                  'flex items-center gap-2 rounded-[22px] bg-mf-neutral-100 py-3 pl-3 pr-4 transition-[filter,opacity] duration-300',
                  status === 'pending' && 'opacity-50 blur-[2px]',
                )}
              >
                <span className="relative flex size-5 shrink-0 items-center justify-center">
                  {status === 'active' && (
                    <Lottie animationData={loaderCircle} className="size-5" />
                  )}
                  {status === 'done' && (
                    <img src={processingCheck} alt="" className="size-5" />
                  )}
                  {status === 'pending' && (
                    <span className="size-[15px] rounded-full border-[1.5px] border-mf-neutral-400" />
                  )}
                </span>
                <span
                  className={cn(
                    'text-[15px] font-bold leading-5 whitespace-nowrap',
                    status === 'pending' ? 'text-mf-neutral-400' : 'text-mf-neutral-900',
                  )}
                >
                  {label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
